import { useState, type FormEvent } from "react";
import { LoaderCircle } from "lucide-react";

import type { OverviewAttention, OverviewAttentionLine } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import {
  ATTENTION_GROUPS,
  ATTENTION_LINES,
  type AttentionLineMeta
} from "./attention-lines";

type Draft = Record<OverviewAttentionLine, { enabled: boolean; threshold: string }>;

function toDraft(attention: OverviewAttention): Draft {
  const draft = {} as Draft;
  for (const { key } of ATTENTION_LINES) {
    draft[key] = { enabled: attention[key].enabled, threshold: String(attention[key].threshold) };
  }
  return draft;
}

/** O limite digitado, ou `null` se não vale pra linha: fora da faixa, ou
 *  com fração onde só cabe inteiro (o CENA aceita uma casa). `Number("")`
 *  é 0, por isso o vazio é recusado antes. */
function parseThreshold(line: AttentionLineMeta, text: string): number | null {
  if (text.trim() === "") return null;
  const value = Number(text.replace(",", "."));
  if (!Number.isFinite(value) || value < line.min || value > line.max) return null;
  const valid = line.unit === "cena" ? Math.round(value * 10) / 10 === value : Number.isInteger(value);
  return valid ? value : null;
}

/**
 * O ajuste do painel "Pontos de atenção" (item 25 da "Fila da galera",
 * 2026-11-01; quinze linhas desde 2026-11-02): quais linhas automáticas
 * aparecem e em que limite cada uma acende. Vale pra empresa inteira; só
 * chega aqui quem é Especialista para cima (a rota decide).
 *
 * `visible` diz quais linhas fazem sentido pra empresa (as do CENA somem
 * com o CENA oculto, o NEUTRO final só existe na escala de 4 níveis). As
 * escondidas vão no salvamento como estavam: o painel é salvo inteiro.
 *
 * O limite de linha desligada não é validado: ele fica guardado, mas não
 * vale nada enquanto a linha estiver desligada.
 */
export function AttentionEditor({
  attention,
  visible,
  onSave,
  onClose
}: {
  attention: OverviewAttention;
  visible: (key: OverviewAttentionLine) => boolean;
  onSave: (next: OverviewAttention) => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(attention));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function change(key: OverviewAttentionLine, patch: Partial<Draft[OverviewAttentionLine]>) {
    setDraft(current => ({ ...current, [key]: { ...current[key], ...patch } }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next = { ...attention };
    for (const line of ATTENTION_LINES) {
      if (!visible(line.key)) continue;
      const entry = draft[line.key];
      const parsed = parseThreshold(line, entry.threshold);
      if (entry.enabled && parsed === null) {
        const range = `${line.min.toLocaleString("pt-BR")} a ${line.max.toLocaleString("pt-BR")}`;
        const kind = line.unit === "cena" ? "um número com até uma casa decimal" : "um número inteiro";
        setError(`${line.label}: o limite precisa ser ${kind} de ${range}.`);
        return;
      }
      next[line.key] = { enabled: entry.enabled, threshold: parsed ?? attention[line.key].threshold };
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      onClose();
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível salvar os pontos de atenção."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="attention-editor" onSubmit={event => void submit(event)} aria-label="Ajustar pontos de atenção">
      {ATTENTION_GROUPS.map(group => {
        const lines = ATTENTION_LINES.filter(line => line.group === group.key && visible(line.key));
        if (!lines.length) return null;
        return (
          <fieldset key={group.key} className="attention-editor-group">
            <legend>{group.label}</legend>
            {lines.map(line => (
              <div key={line.key} className="attention-editor-row">
                <label className="form-field-inline">
                  <input
                    type="checkbox"
                    checked={draft[line.key].enabled}
                    onChange={event => change(line.key, { enabled: event.target.checked })}
                  />
                  <span>{line.label}</span>
                </label>
                <label className="form-field">
                  <span>{line.thresholdLabel}{line.suffix ? ` (${line.suffix})` : ""}</span>
                  <input
                    type="number"
                    inputMode={line.unit === "cena" ? "decimal" : "numeric"}
                    min={line.min}
                    max={line.max}
                    step={line.step}
                    value={draft[line.key].threshold}
                    disabled={!draft[line.key].enabled}
                    aria-label={`${line.label}: ${line.thresholdLabel.toLocaleLowerCase("pt-BR")}`}
                    onChange={event => change(line.key, { threshold: event.target.value })}
                  />
                </label>
                {line.hint && <small>{line.hint}</small>}
              </div>
            ))}
          </fieldset>
        );
      })}
      <p className="form-hint">
        Vale para toda a empresa. Abaixo do limite, a linha continua com o número, mas sem
        alerta e fora do total de atenção do topo.
      </p>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="attention-editor-actions">
        <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>Cancelar</button>
        <button type="submit" className="primary-button" disabled={saving}>
          {saving && <LoaderCircle className="spin" size={14} aria-hidden="true"/>}Salvar
        </button>
      </div>
    </form>
  );
}
